import type { SearchEntry } from "../../redux/features/savedSearchesSlice";
import {
  normalizeSavedSearchUrlInput,
  shouldSkipPersistingSavedSearchUrl,
} from "./normalize-saved-search-url";
import { savedSearchAlertId } from "./saved-search-alert-id";
import { distillSearchHistory, TRANSIENT_DWELL_MS } from "./search-history-policy";
import type { ServerSavedSearch } from "./server-saved-searches";

/** Mirrors MAX_SAVED_SEARCHES in redux/features/savedSearchesSlice.ts. */
const MAX_SAVED_SEARCHES = 50;

/**
 * How long a local search must sit untouched before the account-sync pass writes it through to
 * the server. Aligned with TRANSIENT_DWELL_MS (search-history-policy.ts): once the user has
 * stayed on a search this long it can no longer be collapsed into a refinement, so it is worth
 * an account item; before that, uploading it would mean one write per keystroke pause plus a
 * delete once the refinement lands. Nothing is lost by waiting: the local list is persisted, and
 * the next SRP or hub mount uploads whatever settled in the meantime. Only cross-device
 * visibility is delayed.
 */
export const SAVE_SYNC_DEBOUNCE_MS = TRANSIENT_DWELL_MS;

/** An account item the user acted on (alert, name or folder), as opposed to one the SRP recorded. */
export function isDeliberateServerSavedSearch(item: Pick<ServerSavedSearch, "alertEnabled" | "label" | "folderId">): boolean {
  return item.alertEnabled === true || Boolean(item.label?.trim()) || Boolean(item.folderId?.trim());
}

/** Canonical stored form: path + search, origin stripped (same basis as savedSearchAlertId). */
export function canonicalSavedSearchPath(storedUrl: string): string {
  return toPathAndSearch(storedUrl);
}

function toPathAndSearch(storedUrl: string): string {
  const normalized = normalizeSavedSearchUrlInput(storedUrl) || storedUrl.trim();
  try {
    const parsed = new URL(normalized, "https://www.jobcrawls.com");
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return normalized;
  }
}

export interface SavedSearchUpload {
  id: string;
  url: string;
  timestamp: number;
}

export interface SavedSearchSyncPlan {
  /** Local searches with no server item, settled long enough to upload — to PUT (version 0). */
  uploads: SavedSearchUpload[];
  /** Union of local + server for setSavedSearches, newest first, capped. */
  mergedLocal: SearchEntry[];
  /** mergedLocal differs from the local list — dispatch only then. */
  changed: boolean;
  /**
   * Earliest epoch ms at which a currently-unsettled, not-yet-uploaded local search would
   * become upload-eligible; null if nothing is pending. Callers use this to schedule a retry
   * pass instead of waiting for the next unrelated remount to notice the entry has settled.
   */
  nextSettleCheckAt: number | null;
}

/**
 * One-shot account sync for saved searches (spec §8): upload what the account
 * is missing, pull what other devices saved. Deletions are not reconciled here
 * (no tombstones) — the management UI deletes both sides directly.
 *
 * `now` is injectable for tests; callers should otherwise leave it as the default.
 */
export function computeSavedSearchSyncPlan(
  local: SearchEntry[],
  server: ServerSavedSearch[],
  now: number = Date.now()
): SavedSearchSyncPlan {
  const serverIds = new Set(server.map((s) => s.id));

  const normalizedLocal: SearchEntry[] = [];
  const seenLocalIds = new Set<string>();
  for (const entry of local) {
    if (typeof entry?.url !== "string") continue;
    const normalizedUrl = toPathAndSearch(entry.url);
    if (shouldSkipPersistingSavedSearchUrl(normalizedUrl)) continue;
    const id = savedSearchAlertId(normalizedUrl);
    if (seenLocalIds.has(id)) continue;
    seenLocalIds.add(id);
    normalizedLocal.push({
      url: normalizedUrl,
      timestamp: entry.timestamp,
      ...(entry.pinned ? { pinned: true } : {}),
    });
  }

  const unsynced = normalizedLocal.filter((entry) => !serverIds.has(savedSearchAlertId(entry.url)));

  const uploads: SavedSearchUpload[] = unsynced
    .filter((entry) => now - entry.timestamp >= SAVE_SYNC_DEBOUNCE_MS)
    .map((entry) => ({ id: savedSearchAlertId(entry.url), url: entry.url, timestamp: entry.timestamp }));

  const pendingUnsettled = unsynced.filter((entry) => now - entry.timestamp < SAVE_SYNC_DEBOUNCE_MS);
  const nextSettleCheckAt = pendingUnsettled.length
    ? Math.min(...pendingUnsettled.map((entry) => entry.timestamp + SAVE_SYNC_DEBOUNCE_MS))
    : null;

  const pulls: SearchEntry[] = server
    .filter((s) => !seenLocalIds.has(s.id) && !shouldSkipPersistingSavedSearchUrl(s.url))
    .map((s) => ({
      url: toPathAndSearch(s.url),
      timestamp: typeof s.createdAt === "number" ? s.createdAt : 0,
      ...(isDeliberateServerSavedSearch(s) ? { pinned: true } : {}),
    }));

  // Distilled the same way the reducer distils local writes, so history recorded by another
  // device (or before the policy existed) is cleaned on the way in rather than shown as-is.
  const mergedLocal = distillSearchHistory([...normalizedLocal, ...pulls]).slice(0, MAX_SAVED_SEARCHES);

  const changed =
    mergedLocal.length !== local.length ||
    mergedLocal.some((entry, index) => local[index]?.url !== entry.url);

  return { uploads, mergedLocal, changed, nextSettleCheckAt };
}
